import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  ConfirmationPanel,
  ConnectionStatus,
  DataList,
  FilterChips,
  Dialog,
  DistributionProgress,
  EmptyState,
  RoomGrid,
  SelectField,
  TextField,
} from '@rooming/ui';
import { formatInZone, missingChips, useLiveBoard, type BoardRoom, type BoardSlot } from '@rooming/web-core';
import { useEffect, useState } from 'react';
import { api, backoffice, type HistoryEntry, type RosterEntry } from '../api';
import { Loaded, ReasonDialog, SEX_TEXT, STAY_STATUS, useCommand } from '../common';
import { PassengerDetailModal } from '../PassengerDetailModal';
import { useSession } from '../session';

/**
 * Who is looking decides what the board lets them do (the API enforces it; this only hides refused actions):
 *  - `manage`: the administrator, who may move or release passengers with a reason;
 *  - `hotel`: only the hotel's room number is editable;
 *  - `read`: everything read-only (the guide).
 */
export type BoardMode = 'manage' | 'hotel' | 'read';

type Pending =
  | { readonly kind: 'release'; readonly room: BoardRoom; readonly slot: BoardSlot }
  | { readonly kind: 'assign'; readonly room: BoardRoom; readonly slot: BoardSlot }
  | { readonly kind: 'label'; readonly room: BoardRoom }
  | { readonly kind: 'reopen' }
  | { readonly kind: 'exchange' };

/**
 * The live distribution of one stay for the backoffice and the external portal: the same board as the passenger app
 * (same component, same real-time rules) with names, the progress panel and — depending on the mode — the staff
 * commands (move / release with a reason) or the hotel's single field.
 */
export function StayBoard({
  stayId,
  mode,
  showProgress,
  showHistory = false,
  canReopen = false,
  canExchange = false,
  canExport = false,
  canSeeDocuments = false,
  timeZone = 'UTC',
}: {
  readonly stayId: string;
  readonly mode: BoardMode;
  readonly showProgress: boolean;
  readonly showHistory?: boolean;
  /** Reversing a confirmation: administrators only (DR-204). */
  readonly canReopen?: boolean;
  /** Exchanging two passengers on their behalf: administrators only (prompt11 §8). */
  readonly canExchange?: boolean;
  /** Downloading the rooming list as a file: hotel, guide and administrator. */
  readonly canExport?: boolean;
  /** Reading the passengers' document numbers: hotel and guide (prompt11 §11, §12). An explicit, audited read. */
  readonly canSeeDocuments?: boolean;
  /** The hotel's time zone, to show when the distribution was confirmed. */
  readonly timeZone?: string;
}) {
  const { signOut } = useSession();
  const progressKey = ['distribution', stayId] as const;
  const historyKey = ['history', stayId] as const;
  const { board, connection, resync } = useLiveBoard(api, stayId, {
    alsoInvalidate: [progressKey, historyKey],
    invalidateOnAnyChange: true,
    onSessionEnded: () => void signOut(),
  });
  const progress = useQuery({
    queryKey: progressKey,
    queryFn: () => backoffice.distribution(stayId),
    enabled: showProgress,
  });
  const history = useQuery({ queryKey: historyKey, queryFn: () => backoffice.history(stayId), enabled: showHistory });
  // The status of the stay changes from other screens too (the signatory confirms, an administrator reopens or
  // closes): every list of stays — the group's summary, the dashboard — is re-read when this board learns of it.
  const queryClient = useQueryClient();
  const liveStatus = board.data?.status;
  useEffect(() => {
    if (liveStatus !== undefined) void queryClient.invalidateQueries({ queryKey: ['stays'] });
  }, [liveStatus, queryClient]);
  const { busy, run } = useCommand();
  const [pending, setPending] = useState<Pending>();
  const [memberId, setMemberId] = useState('');
  const [otherMemberId, setOtherMemberId] = useState('');
  const [label, setLabel] = useState('');
  // The hotel's numbers being typed, by room, until "Guardar cambios" sends them all (owner, 2026-10-07).
  const [drafts, setDrafts] = useState<ReadonlyMap<string, string>>(new Map());
  // The passenger whose card is open (administrator, hotel, guide).
  const [detailId, setDetailId] = useState<string>();
  const close = (): void => {
    setPending(undefined);
    setMemberId('');
    setOtherMemberId('');
    setLabel('');
  };
  const keys = [['board', stayId], progressKey, historyKey] as const;

  async function confirm(reason?: string): Promise<void> {
    if (pending === undefined) return;
    let ok = false;
    if (pending.kind === 'release' && pending.slot.occupant !== undefined && reason !== undefined) {
      const occupant = pending.slot.occupant;
      ok = await run(() => backoffice.release(stayId, occupant.memberId, reason), 'Plaza liberada.', keys);
    } else if (pending.kind === 'assign' && reason !== undefined) {
      ok = await run(() => backoffice.assign(stayId, memberId, pending.slot.id, reason), 'Pasajero asignado.', keys);
    } else if (pending.kind === 'reopen' && reason !== undefined) {
      ok = await run(() => backoffice.reopenDistribution(stayId, reason), 'Distribución reabierta.', [
        ...keys,
        ['stays'],
      ]);
    } else if (pending.kind === 'exchange' && reason !== undefined) {
      ok = await run(
        () => backoffice.administratorExchange(stayId, memberId, otherMemberId, reason),
        'Intercambio realizado.',
        keys,
      );
    } else if (pending.kind === 'label') {
      const value = label.trim() === '' ? null : label.trim();
      ok = await run(
        () => backoffice.setPhysicalLabel(pending.room.id, value, reason),
        'Habitación del hotel guardada.',
        keys,
      );
    }
    if (ok) close();
  }

  return (
    <Loaded query={board}>
      {(data) => {
        const status = STAY_STATUS[data.status] ?? { text: data.status, tone: 'neutral' as const };
        const missing = progress.data?.missing ?? [];
        const accepting = data.status === 'OPEN' || data.status === 'REOPENED';
        const confirmed = data.status === 'CONFIRMED';
        // Confirmed (prompt10 §2.1): staff can still place a passenger WITHOUT a slot in a free one; nothing else.
        const canAssign = mode === 'manage' && (accepting || confirmed);
        const canRelease = mode === 'manage' && accepting;
        const confirmation = progress.data?.confirmation ?? null;
        const canLabel = mode === 'hotel' || mode === 'manage';
        // prompt11 §8: the administrator exchanges two passengers who hold a place — also once confirmed.
        const exchangeable = canExchange && (accepting || confirmed);
        const placed = data.rooms.flatMap((room) =>
          room.slots.flatMap((slot) =>
            slot.occupant === undefined
              ? []
              : [
                  {
                    value: slot.occupant.memberId,
                    label: `${slot.occupant.lastNames}, ${slot.occupant.firstNames} — ${room.label}`,
                    roomId: room.id,
                  },
                ],
          ),
        );
        const roomOfMember = (id: string): string | undefined => placed.find((p) => p.value === id)?.roomId;
        return (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone={status.tone}>{status.text}</Badge>
              <ConnectionStatus state={connection} />
              <Button variant="ghost" onClick={resync}>
                Actualizar
              </Button>
              {canExport && (
                <Button
                  variant="secondary"
                  loading={busy}
                  onClick={() => void run(() => backoffice.downloadRoomingList(stayId), 'Rooming list descargada.')}
                >
                  Descargar rooming list (CSV)
                </Button>
              )}
              {exchangeable && (
                <Button
                  variant="secondary"
                  disabled={placed.length < 2}
                  onClick={() => {
                    setPending({ kind: 'exchange' });
                  }}
                >
                  Intercambiar dos pasajeros
                </Button>
              )}
            </div>
            {mode === 'manage' && !accepting && (
              <Alert tone="info">
                {confirmed
                  ? 'La distribución está confirmada: solo se puede ubicar en una plaza libre a quien todavía no tiene. Los demás cambian por intercambio entre pasajeros; si un pasajero no puede ingresar, un administrador hace el intercambio por él o reabre la distribución.'
                  : data.status === 'CLOSED'
                    ? 'La estadía está cerrada: ya no cambia. Sigue disponible para consulta y descarga.'
                    : 'Los pasajeros solo se pueden mover o liberar con la selección abierta.'}
              </Alert>
            )}

            {showProgress && progress.data !== undefined && (
              <DistributionProgress
                assigned={progress.data.assigned}
                passengers={progress.data.passengers}
                percent={progress.data.percentAssigned}
                freeSlots={progress.data.freeSlots}
                missing={missingChips(progress.data)}
              />
            )}
            {showProgress && progress.data !== undefined && data.status !== 'DRAFT' && data.status !== 'CLOSED' && (
              <ConfirmationPanel
                confirmedText={
                  confirmation === null
                    ? undefined
                    : `Confirmada el ${formatInZone(confirmation.confirmedAt, timeZone)} con ${String(confirmation.assigned)} de ${String(confirmation.eligible)} pasajeros con plaza.`
                }
                thresholdPercent={progress.data.thresholdPercent}
                percent={progress.data.percentAssigned}
                confirmable={progress.data.confirmable}
                viewerIsSigner={false}
                action={
                  canReopen && data.status === 'CONFIRMED' ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setPending({ kind: 'reopen' });
                      }}
                    >
                      Reabrir distribución
                    </Button>
                  ) : undefined
                }
              />
            )}

            {data.rooms.length === 0 ? (
              <EmptyState title="La estadía todavía no tiene habitaciones" />
            ) : (
              <div className="flex flex-col gap-6">
                {(
                  [
                    ['PASSENGER', 'Habitaciones de pasajeros'],
                    ['COMPANION', 'Habitaciones de acompañantes'],
                  ] as const
                ).map(([audience, title]) => {
                  const rooms = data.rooms.filter((room) => (room.audience ?? 'PASSENGER') === audience);
                  if (rooms.length === 0) return null;
                  return (
                    <section key={audience} aria-labelledby={`block-${audience}`} className="flex flex-col gap-3">
                      {/* The titles appear only when there are companions' rooms: otherwise the board is as it always was. */}
                      {data.rooms.some((room) => room.audience === 'COMPANION') && (
                        <h3 id={`block-${audience}`} className="text-lg font-bold">
                          {title}
                        </h3>
                      )}
                      <RoomGrid
                        rooms={rooms}
                        slotAction={(room, slot) => {
                          if (slot.state === 'FREE') {
                            // The companions choose their own bed (nobody is assigned for them from here).
                            if (!canAssign || room.audience === 'COMPANION') return undefined;
                            return missing.length === 0
                              ? undefined
                              : {
                                  verb: 'Asignar un pasajero',
                                  text: '+ Asignar pasajero',
                                  run: () => {
                                    setPending({ kind: 'assign', room, slot });
                                  },
                                };
                          }
                          const occupant = slot.occupant;
                          if (occupant === undefined) return undefined;
                          return canRelease
                            ? {
                                verb: 'Liberar',
                                run: () => {
                                  setPending({ kind: 'release', room, slot });
                                },
                              }
                            : {
                                verb: 'Ver ficha',
                                run: () => {
                                  setDetailId(occupant.memberId);
                                },
                              };
                        }}
                        roomFooter={(room) =>
                          data.status === 'CLOSED' || !canLabel ? null : mode === 'hotel' ? (
                            <div className="border-t border-line pt-3">
                              <TextField
                                label="Nro. de habitación del hotel"
                                aria-label={`Nro. de habitación del hotel de ${room.label}`}
                                hint="Por ejemplo 204. Vacío para quitarlo."
                                maxLength={16}
                                value={drafts.get(room.id) ?? room.physicalLabel ?? ''}
                                onChange={(event) => {
                                  const next = new Map(drafts);
                                  next.set(room.id, event.target.value);
                                  setDrafts(next);
                                }}
                              />
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              aria-label={`Habitación del hotel de ${room.label}`}
                              onClick={() => {
                                setLabel(room.physicalLabel ?? '');
                                setPending({ kind: 'label', room });
                              }}
                            >
                              {room.physicalLabel === null || room.physicalLabel === undefined
                                ? 'Cargar habitación del hotel'
                                : 'Cambiar habitación del hotel'}
                            </Button>
                          )
                        }
                      />
                    </section>
                  );
                })}
              </div>
            )}

            {mode === 'hotel' &&
              data.status !== 'CLOSED' &&
              (() => {
                const changed = data.rooms.filter((room) => {
                  const draft = drafts.get(room.id);
                  return draft !== undefined && draft.trim() !== (room.physicalLabel ?? '');
                });
                return (
                  <div className="sticky bottom-3 z-20 flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-base" aria-live="polite">
                      {changed.length === 0
                        ? 'Cargá los números de habitación del hotel y guardalos juntos.'
                        : `${String(changed.length)} ${changed.length === 1 ? 'número sin guardar' : 'números sin guardar'}.`}
                    </p>
                    <Button
                      loading={busy}
                      disabled={changed.length === 0}
                      onClick={() => {
                        const saved: string[] = [];
                        void run(
                          async () => {
                            // One request per changed room (the existing endpoint); each one is audited.
                            for (const room of changed) {
                              const value = (drafts.get(room.id) ?? '').trim();
                              await backoffice.setPhysicalLabel(room.id, value === '' ? null : value, undefined);
                              saved.push(room.id);
                            }
                          },
                          changed.length === 1 ? 'Número guardado.' : `${String(changed.length)} números guardados.`,
                          keys,
                        ).finally(() => {
                          // What was saved leaves the draft; what failed stays typed, to retry.
                          setDrafts((current) => new Map([...current].filter(([id]) => !saved.includes(id))));
                        });
                      }}
                    >
                      Guardar cambios
                    </Button>
                  </div>
                );
              })()}

            <RosterList
              stayId={stayId}
              version={data.version}
              canSeeDocuments={canSeeDocuments}
              rooms={data.rooms}
              detailId={detailId}
              onDetail={setDetailId}
            />

            {showHistory && (
              <section className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">Historial de asignaciones</h2>
                <Loaded query={history}>
                  {(page) => (
                    <DataList<HistoryEntry>
                      caption="Historial de asignaciones"
                      rows={page.items}
                      rowKey={(entry) => entry.id}
                      empty="Todavía no hubo movimientos."
                      columns={[
                        { header: 'Cuándo (UTC)', cell: (e) => e.occurredAt.replace('T', ' ').replace('Z', '') },
                        { header: 'Pasajero', cell: (e) => e.memberName },
                        { header: 'Acción', cell: (e) => HISTORY_TEXT[e.action] ?? e.action },
                        { header: 'De → a', cell: (e) => `${e.fromRoom ?? '—'} → ${e.toRoom ?? '—'}` },
                        { header: 'Quién', cell: (e) => e.actorName ?? '—' },
                        { header: 'Motivo', cell: (e) => e.reason ?? '—' },
                      ]}
                    />
                  )}
                </Loaded>
              </section>
            )}

            <ReasonDialog
              open={pending?.kind === 'reopen'}
              title="¿Reabrir la distribución confirmada?"
              confirmLabel="Reabrir"
              danger
              busy={busy}
              onConfirm={(reason) => void confirm(reason)}
              onClose={close}
            >
              <p>
                La confirmación del firmante queda sin efecto (sigue en el registro). Los pasajeros podrán volver a
                cambiar de habitación y el firmante tendrá que confirmar de nuevo.
              </p>
            </ReasonDialog>

            <ReasonDialog
              open={pending?.kind === 'exchange'}
              title="Intercambiar dos pasajeros"
              confirmLabel="Intercambiar"
              busy={busy}
              disabled={
                memberId === '' || otherMemberId === '' || roomOfMember(memberId) === roomOfMember(otherMemberId)
              }
              onConfirm={(reason) => void confirm(reason)}
              onClose={close}
            >
              <p>
                Usalo solo cuando un pasajero no puede hacerlo por sí mismo (por ejemplo, no puede ingresar al sistema).
                Los dos cambian de habitación en el mismo momento. Queda registrado que lo hiciste vos, con el motivo.
              </p>
              <SelectField
                label="Primer pasajero"
                placeholder="Elegí un pasajero"
                options={placed}
                value={memberId}
                onChange={(event) => {
                  setMemberId(event.target.value);
                }}
              />
              <SelectField
                label="Segundo pasajero"
                placeholder="Elegí un pasajero de otra habitación"
                options={placed.filter((p) => p.value !== memberId && p.roomId !== roomOfMember(memberId))}
                value={otherMemberId}
                onChange={(event) => {
                  setOtherMemberId(event.target.value);
                }}
              />
            </ReasonDialog>

            <ReasonDialog
              open={pending?.kind === 'release'}
              title="¿Liberar esta plaza?"
              confirmLabel="Liberar"
              danger
              busy={busy}
              onConfirm={(reason) => void confirm(reason)}
              onClose={close}
            >
              {pending?.kind === 'release' && (
                <p>
                  {pending.slot.occupant?.firstNames} {pending.slot.occupant?.lastNames} quedará sin plaza en{' '}
                  {pending.room.label}.
                </p>
              )}
            </ReasonDialog>

            <ReasonDialog
              open={pending?.kind === 'assign'}
              title="Asignar un pasajero"
              confirmLabel="Asignar"
              busy={busy}
              disabled={memberId === ''}
              onConfirm={(reason) => void confirm(reason)}
              onClose={close}
            >
              {pending?.kind === 'assign' && (
                <>
                  <p>
                    Plaza {pending.slot.position} de {pending.room.label}.
                  </p>
                  <SelectField
                    label="Pasajero sin plaza"
                    placeholder="Elegí un pasajero"
                    options={missing.map((m) => ({
                      value: m.memberId,
                      label: `${m.lastNames}, ${m.firstNames} (${SEX_TEXT[m.sex] ?? m.sex})`,
                    }))}
                    value={memberId}
                    onChange={(event) => {
                      setMemberId(event.target.value);
                    }}
                  />
                </>
              )}
            </ReasonDialog>

            {mode === 'hotel' ? (
              <Dialog
                open={pending?.kind === 'label'}
                title="Habitación del hotel"
                onClose={close}
                actions={
                  <>
                    <Button variant="secondary" disabled={busy} onClick={close}>
                      Cancelar
                    </Button>
                    <Button loading={busy} onClick={() => void confirm()}>
                      Guardar
                    </Button>
                  </>
                }
              >
                <LabelField
                  room={pending?.kind === 'label' ? pending.room : undefined}
                  value={label}
                  onChange={setLabel}
                />
              </Dialog>
            ) : (
              <ReasonDialog
                open={pending?.kind === 'label'}
                title="Habitación del hotel"
                confirmLabel="Guardar"
                busy={busy}
                onConfirm={(reason) => void confirm(reason)}
                onClose={close}
              >
                <LabelField
                  room={pending?.kind === 'label' ? pending.room : undefined}
                  value={label}
                  onChange={setLabel}
                />
              </ReasonDialog>
            )}
          </div>
        );
      }}
    </Loaded>
  );
}

function LabelField({
  room,
  value,
  onChange,
}: {
  readonly room: BoardRoom | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <TextField
      label={`Número o nombre real de ${room?.label ?? 'la habitación'}`}
      hint="Letras, números, espacios, punto, barra o guion (máx. 16). Vacío para quitarlo."
      maxLength={16}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

const HISTORY_TEXT: Record<string, string> = {
  RESERVE: 'Reservó',
  CHANGE: 'Cambió',
  RELEASE: 'Liberó',
  ADMIN_ASSIGN: 'Asignado por el personal',
  ADMIN_MOVE: 'Movido por el personal',
  ADMIN_RELEASE: 'Liberado por el personal',
  CANCEL_MEMBER: 'Baja del integrante',
  SWAP: 'Intercambio acordado',
  ADMIN_SWAP: 'Intercambio hecho por un administrador',
};

type RosterFilter = 'all' | 'with' | 'without';

/**
 * Every passenger of the group and where they are (staff, hotel, guide — prompt10 §10–§12), filtered by who has a
 * place. The hotel and the guide may also read the document numbers (prompt11 §11, §12): only when they ask for them —
 * each read is audited — and only kept in memory while this screen is open. Each name opens the passenger's card.
 */
function RosterList({
  stayId,
  version,
  canSeeDocuments,
  rooms,
  detailId,
  onDetail,
}: {
  readonly stayId: string;
  readonly version: number;
  readonly canSeeDocuments: boolean;
  readonly rooms: readonly BoardRoom[];
  readonly detailId: string | undefined;
  readonly onDetail: (memberId: string | undefined) => void;
}) {
  const roster = useQuery({
    queryKey: ['roster', stayId, version],
    queryFn: async () => (await backoffice.roster(stayId)).items,
  });
  const { busy, run } = useCommand();
  const [documents, setDocuments] = useState<ReadonlyMap<string, string>>();
  const [filter, setFilter] = useState<RosterFilter>('all');
  const loadDocuments = (): void =>
    void run(async () => {
      const items = (await backoffice.roster(stayId, true)).items;
      setDocuments(new Map(items.map((p) => [p.memberId, p.document?.number ?? '—'])));
    }, 'Documentos cargados. La consulta queda registrada.');
  const all = roster.data ?? [];
  const detail = all.find((p) => p.memberId === detailId);
  const bedOf = (memberId: string): { room: string; position: number } | undefined => {
    for (const room of rooms) {
      const slot = room.slots.find((candidate) => candidate.occupant?.memberId === memberId);
      if (slot !== undefined) return { room: room.label, position: slot.position };
    }
    return undefined;
  };
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold">Pasajeros</h2>
        {canSeeDocuments &&
          (documents === undefined ? (
            <Button variant="secondary" loading={busy} onClick={loadDocuments}>
              Ver documentos
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() => {
                setDocuments(undefined);
              }}
            >
              Ocultar documentos
            </Button>
          ))}
      </div>
      <FilterChips
        label="Filtrar pasajeros"
        value={filter}
        onChange={setFilter}
        options={[
          ['all', `Todos (${String(all.length)})`],
          ['with', `Con plaza (${String(all.filter((p) => p.room !== null).length)})`],
          ['without', `Sin plaza (${String(all.filter((p) => p.room === null).length)})`],
        ]}
      />
      <PassengerDetailModal
        passenger={detail}
        bed={detail === undefined ? undefined : bedOf(detail.memberId)}
        document={detail === undefined ? undefined : documents?.get(detail.memberId)}
        documentAction={
          canSeeDocuments ? (
            <Button variant="secondary" loading={busy} onClick={loadDocuments}>
              Ver documento
            </Button>
          ) : (
            'No disponible en esta vista'
          )
        }
        onClose={() => {
          onDetail(undefined);
        }}
      />
      <Loaded query={roster}>
        {(items) => (
          <DataList<RosterEntry>
            caption="Pasajeros del grupo"
            rows={items.filter((p) => filter === 'all' || (filter === 'with') === (p.room !== null))}
            rowKey={(p) => p.memberId}
            empty={items.length === 0 ? 'El grupo todavía no tiene pasajeros.' : 'Nadie en este filtro.'}
            columns={[
              {
                header: 'Pasajero',
                cell: (p) => (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="min-h-11 text-left font-semibold text-brand-700 underline-offset-4 hover:underline"
                      aria-label={`Ver la ficha de ${p.firstNames} ${p.lastNames}`}
                      onClick={() => {
                        onDetail(p.memberId);
                      }}
                    >
                      {p.lastNames}, {p.firstNames}
                    </button>
                    {/* DR-408: Pendiente de activar = hay que pasarle el enlace del grupo. */}
                    {p.accountState === 'PENDING' && <Badge tone="warning">Pendiente de activar</Badge>}
                    {p.accountState === 'ACTIVATED' && <Badge tone="success">Activo</Badge>}
                  </span>
                ),
              },
              {
                header: 'Tipo',
                cell: (p) =>
                  p.role === 'COMPANION' ? (
                    <Badge tone="info">Acompañante</Badge>
                  ) : (
                    <Badge tone="neutral">Pasajero</Badge>
                  ),
              },
              { header: 'Sexo', cell: (p) => SEX_TEXT[p.sex] ?? p.sex },
              ...(documents === undefined
                ? []
                : [
                    {
                      header: 'Documento',
                      cell: (p: RosterEntry) => <span className="font-mono">{documents.get(p.memberId) ?? '—'}</span>,
                    },
                  ]),
              { header: 'Habitación', cell: (p) => p.room ?? 'Sin plaza' },
              {
                header: 'Habitación del hotel',
                cell: (p) => (p.room === null ? '—' : (p.physicalRoom ?? 'Todavía no disponible')),
              },
            ]}
          />
        )}
      </Loaded>
    </section>
  );
}
